/**
 * API Key Management Component
 *
 * Allows restaurant owners to configure their API keys for enhanced
 * beverage intelligence services.
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./APIKeyManager.css";

const APIKeyManager = ({ onKeysUpdated }) => {
  const [apiKeys, setApiKeys] = useState({
    anthropic: "",
    openai: "",
    vivino: "",
    wineCom: "",
    untappd: "",
    spoonacular: "",
    cocktailDB: "",
  });

  const [isVisible, setIsVisible] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    // Load existing API keys from environment or localStorage
    loadExistingKeys();
  }, []);

  const loadExistingKeys = () => {
    // In a real app, these would be loaded from a secure backend
    // For now, we'll check if they're set in environment
    const keys = {
      anthropic: process.env.REACT_APP_ANTHROPIC_API_KEY || "",
      openai: process.env.REACT_APP_OPENAI_API_KEY || "",
      vivino: process.env.REACT_APP_VIVINO_API_KEY || "",
      wineCom: process.env.REACT_APP_WINE_COM_API_KEY || "",
      untappd: process.env.REACT_APP_UNTAPPD_CLIENT_ID || "",
      spoonacular: process.env.REACT_APP_SPOONACULAR_API_KEY || "",
      cocktailDB: process.env.REACT_APP_COCKTAIL_DB_API_KEY || "",
    };

    setApiKeys(keys);
  };

  const handleKeyChange = (service, value) => {
    setApiKeys((prev) => ({
      ...prev,
      [service]: value,
    }));
  };

  const toggleVisibility = (service) => {
    setIsVisible((prev) => ({
      ...prev,
      [service]: !prev[service],
    }));
  };

  const testAPIKey = async (service, key) => {
    if (!key || key.includes("your_") || key.includes("_here")) {
      return { success: false, message: "Invalid API key format" };
    }

    setTestResults((prev) => ({
      ...prev,
      [service]: { testing: true },
    }));

    try {
      let result;

      switch (service) {
        case "anthropic":
          result = await testAnthropicKey(key);
          break;
        case "spoonacular":
          result = await testSpoonacularKey(key);
          break;
        default:
          result = { success: false, message: "Test not implemented yet" };
      }

      setTestResults((prev) => ({
        ...prev,
        [service]: result,
      }));

      return result;
    } catch (error) {
      const result = { success: false, message: error.message };
      setTestResults((prev) => ({
        ...prev,
        [service]: result,
      }));
      return result;
    }
  };

  const testAnthropicKey = async (key) => {
    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": key,
        "anthropic-version": "2023-06-01",
      },
      body: JSON.stringify({
        model: "claude-3-haiku-20240307",
        max_tokens: 50,
        messages: [{ role: "user", content: "Test" }],
      }),
    });

    if (response.ok) {
      return { success: true, message: "API key is valid" };
    } else {
      const error = await response.json();
      return {
        success: false,
        message: error.error?.message || "Invalid API key",
      };
    }
  };

  const testSpoonacularKey = async (key) => {
    const response = await fetch(
      `https://api.spoonacular.com/food/wine/pairing?food=steak&apiKey=${key}`
    );

    if (response.ok) {
      return { success: true, message: "API key is valid" };
    } else {
      return { success: false, message: "Invalid API key or quota exceeded" };
    }
  };

  const saveKeys = async () => {
    setIsSaving(true);

    try {
      // In a real application, these would be saved securely to a backend
      // For development, we'll just validate them and notify the parent component

      console.log("Saving API keys:", apiKeys);

      // Notify parent component about updated keys
      if (onKeysUpdated) {
        onKeysUpdated(apiKeys);
      }

      alert(
        "API keys saved successfully! Note: In production, these should be configured server-side for security."
      );
    } catch (error) {
      console.error("Error saving API keys:", error);
      alert("Error saving API keys: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const apiServices = [
    {
      key: "anthropic",
      name: "Anthropic Claude",
      description: "AI-powered tasting notes and analysis",
      required: true,
      link: "https://console.anthropic.com/",
    },
    {
      key: "spoonacular",
      name: "Spoonacular",
      description: "Food pairing intelligence",
      required: false,
      link: "https://spoonacular.com/food-api",
    },
    {
      key: "vivino",
      name: "Vivino",
      description: "Wine ratings and community data",
      required: false,
      link: "https://developers.vivino.com/",
    },
    {
      key: "wineCom",
      name: "Wine.com",
      description: "Professional wine database",
      required: false,
      link: "https://api.wine.com/",
    },
    {
      key: "untappd",
      name: "Untappd",
      description: "Beer ratings and brewery data",
      required: false,
      link: "https://untappd.com/api",
    },
    {
      key: "openai",
      name: "OpenAI",
      description: "Alternative AI analysis service",
      required: false,
      link: "https://platform.openai.com/",
    },
  ];

  return (
    <div className="api-key-manager">
      <div className="api-key-header">
        <h3>🔑 API Key Configuration</h3>
        <p>
          Configure your API keys to enable advanced beverage intelligence
          features.
        </p>
      </div>

      <div className="api-services">
        {apiServices.map((service) => (
          <div
            key={service.key}
            className={`api-service ${
              service.required ? "required" : "optional"
            }`}
          >
            <div className="service-header">
              <div className="service-info">
                <h4>
                  {service.name}
                  {service.required && (
                    <span className="required-badge">Required</span>
                  )}
                </h4>
                <p>{service.description}</p>
                <a
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get API Key →
                </a>
              </div>
            </div>

            <div className="key-input-group">
              <div className="input-with-toggle">
                <input
                  type={isVisible[service.key] ? "text" : "password"}
                  value={apiKeys[service.key]}
                  onChange={(e) => handleKeyChange(service.key, e.target.value)}
                  placeholder={`Enter your ${service.name} API key`}
                  className="api-key-input"
                />
                <button
                  type="button"
                  onClick={() => toggleVisibility(service.key)}
                  className="toggle-visibility"
                >
                  {isVisible[service.key] ? "👁️" : "🔒"}
                </button>
              </div>

              <button
                onClick={() => testAPIKey(service.key, apiKeys[service.key])}
                disabled={
                  !apiKeys[service.key] || testResults[service.key]?.testing
                }
                className="test-key-btn"
              >
                {testResults[service.key]?.testing ? "Testing..." : "Test"}
              </button>
            </div>

            {testResults[service.key] && !testResults[service.key].testing && (
              <div
                className={`test-result ${
                  testResults[service.key].success ? "success" : "error"
                }`}
              >
                {testResults[service.key].success ? "✅" : "❌"}{" "}
                {testResults[service.key].message}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="api-key-actions">
        <button
          onClick={saveKeys}
          disabled={isSaving}
          className="save-keys-btn"
        >
          {isSaving ? "Saving..." : "Save API Keys"}
        </button>
      </div>

      <div className="api-key-notes">
        <h4>📋 Important Notes:</h4>
        <ul>
          <li>
            <strong>Security:</strong> API keys should be configured server-side
            in production
          </li>
          <li>
            <strong>Required:</strong> Anthropic Claude is required for
            AI-powered features
          </li>
          <li>
            <strong>Free APIs:</strong> Many features work with free APIs when
            premium keys aren't available
          </li>
          <li>
            <strong>Rate Limits:</strong> Monitor your API usage to avoid
            exceeding quotas
          </li>
        </ul>
      </div>
    </div>
  );
};

APIKeyManager.propTypes = {
  onKeysUpdated: PropTypes.func,
};

export default APIKeyManager;
